import { useCallback, useEffect, useRef } from 'react';
import { SettingsAPI } from '../../utils/api';

/**
 * useTabPersistence Hook
 *
 * Handles saving and loading tabs to/from the backend.
 * Tabs are debounced before saving to reduce API calls.
 *
 * Tabs are WORKSPACE-SCOPED: each workspace's SETTINGS record stores its own
 * openTabs/activeTabId. Saves are pinned to the workspace that was active when
 * the change happened, and the tab set is reloaded whenever the active
 * workspace changes — tabs from one workspace never carry over into another.
 *
 * Persisted with each tab: viewMode, openMissionId, missionDocStack,
 * missionDocScrollById, docStack, docScrollById, etc.
 *
 * Session-only (stripped before save): history, historyIndex.
 */
export function useTabPersistence({ setTabs, setActiveTabId, setIsLoaded, workspaceId }) {
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef(null);
  // Live workspace for pinning saves at the moment they're requested
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  const writeTabs = useCallback(async (newTabs, newActiveTabId, wsId) => {
    try {
      const currentSettings = await SettingsAPI.get({ workspaceId: wsId });

      // Strip session-only navigation history before saving
      const tabsToSave = newTabs.map(({
        history,
        historyIndex,
        ...tab
      }) => tab);

      await SettingsAPI.save(
        {
          ...currentSettings,
          openTabs: tabsToSave,
          activeTabId: newActiveTabId,
        },
        { workspaceId: wsId }
      );

      console.log('[TabsContext] Saved tabs to backend, workspace:', wsId);
    } catch (err) {
      console.error('[TabsContext] Failed to save tabs:', err);
    }
  }, []);

  /**
   * Save tabs to backend (debounced by 1 second).
   * The pending payload captures the CURRENT workspace so a save that fires
   * after a workspace switch still writes to the workspace it belongs to.
   */
  const saveTabs = useCallback((newTabs, newActiveTabId) => {
    pendingSaveRef.current = {
      tabs: newTabs,
      activeTabId: newActiveTabId,
      workspaceId: workspaceIdRef.current,
    };

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      const data = pendingSaveRef.current;
      pendingSaveRef.current = null;
      saveTimerRef.current = null;
      if (!data) return;
      await writeTabs(data.tabs, data.activeTabId, data.workspaceId);
    }, 1000);
  }, [writeTabs]);

  /** Flush any pending debounced save immediately (app quit / hide / workspace switch). */
  const flushPendingSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const data = pendingSaveRef.current;
    if (!data) return;
    pendingSaveRef.current = null;
    // Fire-and-forget; pagehide/beforeunload won't await
    writeTabs(data.tabs, data.activeTabId, data.workspaceId);
  }, [writeTabs]);

  /**
   * Load tabs from backend on mount and on every workspace change.
   * Initializes fresh session-only history for each tab.
   */
  useEffect(() => {
    let cancelled = false;

    const loadTabs = async () => {
      try {
        const settings = await SettingsAPI.get({ workspaceId });
        if (cancelled) return;

        if (settings?.openTabs && Array.isArray(settings.openTabs)) {
          const tabsWithHistory = settings.openTabs.map(tab => ({
            ...tab,
            openMissionId: tab.openMissionId ?? null,
            missionDocStack: Array.isArray(tab.missionDocStack) ? tab.missionDocStack : [],
            missionDocScrollById:
              tab.missionDocScrollById && typeof tab.missionDocScrollById === 'object'
                ? tab.missionDocScrollById
                : {},
            docStack: Array.isArray(tab.docStack) ? tab.docStack : [],
            docScrollById:
              tab.docScrollById && typeof tab.docScrollById === 'object'
                ? tab.docScrollById
                : {},
            // Fresh history with just the current state (not persisted)
            history: [{
              type: tab.type,
              targetId: tab.targetId,
              viewMode: tab.viewMode,
              customViewId: tab.customViewId,
              label: tab.label,
              icon: tab.icon,
            }],
            historyIndex: 0,
          }));
          setTabs(tabsWithHistory);
          setActiveTabId(settings.activeTabId || null);
        }
      } catch (err) {
        console.error('[TabsContext] Failed to load tabs:', err);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };

    // Workspace switch (or first mount): drop the in-memory tab set and load
    // this workspace's own tabs. isLoaded gates the app startup flow, so the
    // restored active tab is re-applied for the new workspace.
    setIsLoaded(false);
    setTabs([]);
    setActiveTabId(null);
    loadTabs();

    const onLeave = () => flushPendingSave();
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('beforeunload', onLeave);

    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('beforeunload', onLeave);
      // Pending payloads are workspace-pinned, so flushing during a switch
      // still writes the old workspace's tabs to the old workspace.
      flushPendingSave();
    };
  }, [workspaceId, setTabs, setActiveTabId, setIsLoaded, flushPendingSave]);

  return { saveTabs, flushPendingSave };
}

export default useTabPersistence;
